import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_LANG,
  languageKeys,
  languages,
  locales,
  rtlLangs,
} from "./constants";
import { ar, en } from "./langs";
import type {
  Direction,
  FlattenedTranslations,
  Language,
  Translations,
} from "./types";

export type {
  Direction,
  FlattenedTranslations,
  Language,
  Translations,
} from "./types";

export { languageKeys } from "./constants";

export const translationKey = <T extends keyof FlattenedTranslations>(
  key: T,
): T => key;

export const allTranslations: Record<Language, Translations> = {
  en,
  ar,
};

const splitKeyCache = new Map<string, string[]>();

export const traversePath = (obj: Translations, key: string): unknown => {
  let current: unknown = obj;
  let paths = splitKeyCache.get(key);
  if (!paths) {
    paths = key.split(".");
    splitKeyCache.set(key, paths);
  }
  for (const path of paths) {
    if (current && typeof current === "object" && path in current) {
      current = (current as Record<string, unknown>)[path];
    } else {
      return undefined;
    }
  }
  return current;
};

function getAllByKey(
  key: keyof FlattenedTranslations,
): Record<Language, string> {
  const result = {} as Record<Language, string>;
  for (const lang of languageKeys) {
    const value = traversePath(allTranslations[lang], key);
    if (typeof value === "string") {
      result[lang] = value;
    } else if (typeof value === "number") {
      result[lang] = value.toString();
    }
  }
  return result;
}

function getInitialLanguage(): Language {
  let lang = DEFAULT_LANG;
  if (typeof window !== "undefined") {
    const langPattern = languageKeys.join("|");
    const match = window.location.pathname.match(
      new RegExp(`^/(${langPattern})(?:/|$)`),
    );
    if (match && languageKeys.includes(match[1] as Language)) {
      lang = match[1] as Language;
    }
    const html = document.documentElement;
    html.lang = lang;
    html.dir = rtlLangs.includes(lang) ? "rtl" : "ltr";
  }
  return lang;
}

let currentLanguage: Language = getInitialLanguage();

export function getCurrentLanguage(): Language {
  return currentLanguage;
}

export function getTranslations(): Translations {
  return allTranslations[currentLanguage];
}

function isRTL(language: Language): boolean {
  return rtlLangs.includes(language);
}

const langChangeListeners = new Set<(language: Language) => void>();

function onLanguageChange(callback: (language: Language) => void): () => void {
  langChangeListeners.add(callback);
  return () => langChangeListeners.delete(callback);
}

function setLanguage(language: Language): void {
  if (currentLanguage === language) return;

  currentLanguage = language;

  const html = document.documentElement;
  html.lang = language;
  html.dir = isRTL(language) ? "rtl" : "ltr";

  langChangeListeners.forEach((listener) => listener(language));
}

export function useI18n() {
  const [currentLang, setCurrentLang] = useState<Language>(
    () => currentLanguage,
  );

  useEffect(() => {
    return onLanguageChange(setCurrentLang);
  }, []);

  const changeLanguage = useCallback((language: Language) => {
    setLanguage(language);
  }, []);

  return useMemo(
    () => ({
      language: currentLang,
      translations: allTranslations[currentLang],
      direction: (isRTL(currentLang) ? "rtl" : "ltr") as Direction,
      locale: locales[currentLang],
      languages,
      languageKeys,
      setLanguage: changeLanguage,
      getAllByKey,
    }),
    [currentLang, changeLanguage],
  );
}
